import { Delta } from "quill/core";

export type QuillClipboardMatcher = [
  string,
  (node: Node, delta: Delta) => Delta,
];

interface ImageLayoutStyleFormat {
  readonly attrName: string;
  readonly keyName: string;
  readonly readValue: (node: HTMLImageElement) => string;
  readonly whitelist: readonly string[];
}

export const IMAGE_LAYOUT_STYLE_FORMATS: readonly ImageLayoutStyleFormat[] = [
  {
    attrName: "float",
    keyName: "float",
    readValue: (node: HTMLImageElement) => node.style.cssFloat,
    whitelist: ["left", "right"],
  },
  {
    attrName: "display",
    keyName: "display",
    readValue: (node: HTMLImageElement) => node.style.display,
    whitelist: ["block"],
  },
  {
    attrName: "margin",
    keyName: "margin",
    readValue: (node: HTMLImageElement) => node.style.margin,
    whitelist: ["auto"],
  },
  {
    attrName: "marginLeft",
    keyName: "margin-left",
    readValue: (node: HTMLImageElement) => node.style.marginLeft,
    whitelist: ["auto"],
  },
  {
    attrName: "marginRight",
    keyName: "margin-right",
    readValue: (node: HTMLImageElement) => node.style.marginRight,
    whitelist: ["auto"],
  },
];

function setFirstOpAttribute(
  firstOp: NonNullable<Delta["ops"]>[number],
  attrName: string,
  value: string,
) {
  firstOp.attributes = firstOp.attributes || {};
  firstOp.attributes[attrName] = value;
}

export const quillClipboardMatchers = {
  img: [
    "img",
    (node, delta) => {
      if (!(node instanceof HTMLImageElement)) {
        return delta;
      }
      const firstOp = delta.ops[0];
      if (!firstOp) {
        return delta;
      }
      if (node.style.width) {
        setFirstOpAttribute(firstOp, "width", node.style.width);
      }
      if (node.style.height) {
        setFirstOpAttribute(firstOp, "height", node.style.height);
      }
      for (const format of IMAGE_LAYOUT_STYLE_FORMATS) {
        const value = format.readValue(node);
        if (format.whitelist.includes(value)) {
          setFirstOpAttribute(firstOp, format.attrName, value);
        }
      }
      return delta;
    },
  ],
  disallowBackground: [
    "*",
    (_, delta) => {
      for (const op of delta.ops) {
        // Disallow background color. That's handled at the email block level.
        delete op.attributes?.background;
      }
      return delta;
    },
  ],
} as const satisfies Record<string, QuillClipboardMatcher>;
